import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { NavLink } from 'react-router-dom';
import TextInput from 'components/atoms/form/TextInput/TextInput';
import FormFooter from 'components/organisms/Form/FormFooter/FormFooter';
import FormModal from 'components/organisms/FormModal/FormModal';
import withFormLayout from '../WithFormLayout';
import RouteConst from '../../../../constants/RouteConst';
import SideNavEntryMapping from '../../../../constants/SideNavEntryMapping';
import SessionConst from '../../../../constants/SessionConst';
import * as FormConst from '../../../../constants/FormConst';
import { asyncMethod } from '../../../../redux/actions';
import * as actionTypes from '../../../../redux/actionTypes';
import AgeTiersService from '../../../../services/AgeTiersService';
import { getSessionItem } from '../../../../utilities/SessionHelper';
import { isArrayNotEmpty, isCompletedHotelInfoHistory } from 'utilities/JSUtil';
import './AgeTiers.scss';

class AgeTiers extends Component {
    constructor(props) {
        super(props);
        const hotelInfoCompletedHistory = getSessionItem(SessionConst.HOTEL_INFO_COMPLETED_HISTORY);

        this.state = {
            hotelCode: getSessionItem(SessionConst.HOTEL_CODE),
            hotelInfo: getSessionItem(SessionConst.HOTEL_INFO),
            initialValues: this.initialValues,
            showDescriptions: false,
            isLoading: true,
            errorMessage: '',
            showErrorModal: false,
            showResetModal: false,
            loadingMessage: FormConst.MODAL_READ_ACTION_TEXT,
            hotelInfoCompletedHistory: hotelInfoCompletedHistory
        };
    }

    initialValues = {
        ageTiers: [
            { id: 1, kind: 'MINOR_AGE_TIER', min: '', max: '', inactive: true },
            { id: 2, kind: 'MINOR_AGE_TIER', min: '', max: '', inactive: true },
            { id: 3, kind: 'MINOR_AGE_TIER', min: '', max: '', inactive: true }
        ]
    };

    validationSchema = Yup.object().shape({
        ageTiers: Yup.array().of(
            Yup.object().shape({
                min: Yup.number()
                    .when('inactive', {
                        is: false,
                        then: (schema) => schema
                            .required('Minimum age is required')
                            .min(0, 'Minimum age must be at least 0')
                            .test('less-than-max', 'Minimum age must be less than maximum age', function(value) {
                                const { max } = this.parent;
                                return value == null || max == null || value < max;
                            })
                            .test('greater-than-prev-max', 'Minimum age must be greater than previous tier maximum age', function(value) {
                                const { parent, from } = this;
                                const allTiers = from[1].value.ageTiers;
                                const currentIndex = allTiers.findIndex(tier => tier === parent);

                                if (currentIndex === 0 || value == null) {
                                    return true;
                                }

                                // Find the previous active tier
                                for (let i = currentIndex - 1; i >= 0; i--) {
                                    if (!allTiers[i].inactive && allTiers[i].max != null) {
                                        return value > allTiers[i].max;
                                    }
                                }

                                return true;
                            }),
                        otherwise: (schema) => schema.nullable()
                    }),
                max: Yup.number()
                    .when('inactive', {
                        is: false,
                        then: (schema) => schema
                            .required('Maximum age is required')
                            .test('greater-than-min', 'Maximum age must be greater than minimum age', function(value) {
                                const { min } = this.parent;
                                return value == null || min == null || value > min;
                            }),
                        otherwise: (schema) => schema.nullable()
                    }),
                inactive: Yup.boolean()
            })
        )
    });

    async componentDidMount() {
        if (isCompletedHotelInfoHistory(this.state.hotelInfoCompletedHistory)) {
            if (isArrayNotEmpty(this.state.hotelInfoCompletedHistory.newEntity.ageTiers?.ageTiers)) {
                const ageTiers = this.state.hotelInfoCompletedHistory.newEntity.ageTiers.ageTiers.map((tier, index) => ({
                    id: index + 1,
                    kind: tier.kind || 'MINOR_AGE_TIER',
                    min: tier.inactive ? '' : tier.min,
                    max: tier.inactive ? '' : tier.max,
                    inactive: tier.inactive !== undefined ? tier.inactive : true
                }));

                this.setState({
                    error: undefined,
                    isLoading: false,
                    initialValues: { ageTiers }
                });
            } else {
                this.setState({ isLoading: false });
            }
        } else {
            await AgeTiersService.getAgeTiers({ hotelCode: this.state.hotelCode })
                .then(response => {
                    if (isArrayNotEmpty(response.ageTiers)) {
                        const ageTiers = response.ageTiers.map((tier, index) => ({
                            id: index + 1,
                            kind: tier.kind || 'MINOR_AGE_TIER',
                            min: tier.min,
                            max: tier.max,
                            inactive: tier.inactive !== undefined ? tier.inactive : true
                        }));

                        this.setState({
                            error: undefined,
                            isLoading: false,
                            initialValues: { ageTiers }
                        });
                    } else {
                        this.setState({ isLoading: false });
                    }
                }).catch(error => {
                    this.setState({
                        isLoading: false,
                        showErrorModal: true,
                        errorMessage: 'There was an error while retrieving Age Tiers data'
                    });
                });
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.count !== this.props.count) {
            if (this.props.errorMessage) {
                this.setState({
                    isLoading: false,
                    showErrorModal: true,
                    errorMessage: this.props.errorDescription
                });
            } else {
                if (this.props.ageTiersResponse && isArrayNotEmpty(this.props.ageTiersResponse.ageTiers)) {
                    const ageTiers = this.props.ageTiersResponse.ageTiers.map((tier, index) => ({
                        id: index + 1,
                        kind: tier.kind || 'MINOR_AGE_TIER',
                        min: tier.min,
                        max: tier.max,
                        inactive: tier.inactive !== undefined ? tier.inactive : true
                    }));

                    this.setState({
                        error: undefined,
                        isLoading: false,
                        initialValues: { ageTiers }
                    });
                } else if (this.state.loadingMessage === FormConst.MODAL_UPDATE_ACTION_TEXT) {
                    // If it was a PUT operation, redirect to policies page
                    this.props.history.push(RouteConst.ROUTE_POLICIES_PAGE);
                } else {
                    // No data found, just stop loading
                    this.setState({ isLoading: false });
                }
            }
        }
    }

    handleInactiveChange = async (ageTiers, index, value, setFieldValue, validateForm) => {
        await setFieldValue(`ageTiers.${index}.inactive`, value);
        if(value) {
            await setFieldValue(`ageTiers.${index}.min`, '');
            await setFieldValue(`ageTiers.${index}.max`, '');
        }

        if (validateForm) {
            validateForm();
        }
    };

    handleSubmit = (formData) => {
        this.props.onPut({ hotelCode: this.state.hotelCode, ageTiers: formData.ageTiers });
        this.setState({ isLoading: true, loadingMessage: FormConst.MODAL_UPDATE_ACTION_TEXT });
    };

    render() {
        return (
            <div className='age-tiers-page'>
                <nav aria-label='breadcrumb'>
                    <ol className='breadcrumb'>
                        <li className='breadcrumb-item'><NavLink to={RouteConst.ROUTE_HOME_PAGE}>Home</NavLink></li>
                        <li className='breadcrumb-item'><NavLink to={RouteConst.ROUTE_POLICIES_PAGE}>Policies</NavLink></li>
                        <li className='breadcrumb-item active' aria-current='page'>Age Tiers</li>
                    </ol>
                </nav>

                <p className='helper-text'>This section is to submit Age Tier details.</p>
                <Formik
                    initialValues={this.state.initialValues}
                    validationSchema={this.validationSchema}
                    enableReinitialize={true}
                    onSubmit={(data, { setSubmitting }) => {
                        setSubmitting(true);
                        this.handleSubmit(data);
                        setSubmitting(false);
                    }}>
                    {({ values, isSubmitting, errors, setFieldValue, validateForm }) => (
                        <Form className='age-tiers-form-element'>
                            <div className='age-tiers-form-fields'>
                                <table className='table age-tiers-table'>
                                    <thead>
                                    <tr>
                                        <th>Tier</th>
                                        <th>Type</th>
                                        <th>Min Age</th>
                                        <th>Max Age</th>
                                        <th>Inactive</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {values.ageTiers.map((tier, index) => (
                                        <tr key={`age-tier-${index}`} className={index % 2 === 0 ? 'cell-dark' : 'cell-light'}>
                                            <td>{tier.id}</td>
                                            <td>{tier.kind}</td>
                                            <td>
                                                <TextInput
                                                    id={`min-age-${index}`}
                                                    name={`ageTiers.${index}.min`}
                                                    type='number'
                                                    title='Min Age'
                                                    disabled={tier.inactive}
                                                    readOnly={tier.inactive}
                                                />
                                            </td>
                                            <td>
                                                <TextInput
                                                    id={`max-age-${index}`}
                                                    name={`ageTiers.${index}.max`}
                                                    type='number'
                                                    title='Max Age'
                                                    disabled={tier.inactive}
                                                    readOnly={tier.inactive}
                                                />
                                            </td>
                                            <td className='text-center'>
                                                <TextInput
                                                    type='checkbox'
                                                    id={`inactive-${index}`}
                                                    name={`ageTiers.${index}.inactive`}
                                                    checked={tier.inactive}
                                                    onChange={e => this.handleInactiveChange(values.ageTiers, index, e.target.checked, setFieldValue, validateForm)}
                                                    title='Inactive'
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                            <FormFooter
                                form={
                                    {
                                        previous: { title: 'Group Policy', route: RouteConst.ROUTE_GROUP_POLICY_PAGE },
                                        next: { title: 'Taxes & Charges', route: RouteConst.ROUTE_HOTEL_TAXES_PAGE }
                                    }
                                }
                                isSubmit
                                submitName={FormConst.SAVE_BUTTON_TEXT}
                                resetName={FormConst.RESET_BUTTON_TEXT}
                                noReset
                                submitDisabled={isSubmitting} />
                            <FormModal
                                id='age-tiers-page-save-modal'
                                title={FormConst.MODAL_UPDATE_ACTION_TITLE}
                                hotelInfo={this.state.hotelInfo}
                                body={<h6>{this.state.loadingMessage}</h6>}
                                show={this.state.isLoading}
                                isLoadingIndicator />
                            <FormModal
                                id='age-tiers-page-error-modal'
                                hotelInfo={this.state.hotelInfo}
                                title={FormConst.MODAL_UPDATE_ACTION_ERROR_TITLE}
                                body={<h5>{this.state.errorMessage}</h5>}
                                isError
                                cancel={() => { this.setState({ showErrorModal: false }) }}
                                show={this.state.showErrorModal} />
                        </Form>
                    )}
                </Formik>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    ageTiersResponse: state.reducer.response,
    errorMessage: state.reducer.errorMessage,
    errorDescription: state.reducer.errorDescription,
    count: state.reducer.count
});

const mapDispatchToProps = dispatch => ({
    onPut: (data) => dispatch(asyncMethod(FormConst.FORM_AGE_TIERS_NAME, actionTypes.ASYNC_ACTIONS.PUT, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withFormLayout(AgeTiers, SideNavEntryMapping.HOTEL_POLICIES));
